import React, { useState, useRef } from 'react';
import {
    PlayIcon,
    PauseIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
    ArrowsPointingOutIcon,
    Cog6ToothIcon,
    BackwardIcon,
    ForwardIcon,
    RectangleStackIcon
} from '@heroicons/react/24/solid';

function CourseVideoPlayer({ videoUrl, title, thumbnail, hasStarted, onVideoEnd }) {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSettings, setShowSettings] = useState(false);
    const [showPlayPauseAnimation, setShowPlayPauseAnimation] = useState(false);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);

            // Trigger animation
            setShowPlayPauseAnimation(true);
            setTimeout(() => setShowPlayPauseAnimation(false), 300);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
        }
    };

    const handleVideoEnded = () => {
        setIsPlaying(false);
        if (onVideoEnd) {
            onVideoEnd();
        }
    };

    const handleLoadedMetadata = () => {
        if (videoRef.current) {
            setDuration(videoRef.current.duration);
        }
    };

    const handleSeek = (e) => {
        const seekTime = (e.target.value / 100) * duration;
        if (videoRef.current) {
            videoRef.current.currentTime = seekTime;
            setCurrentTime(seekTime);
        }
    };

    const handleVolumeChange = (e) => {
        const newVolume = parseFloat(e.target.value);
        setVolume(newVolume);
        if (videoRef.current) {
            videoRef.current.volume = newVolume;
        }
        setIsMuted(newVolume === 0);
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const toggleFullscreen = () => {
        if (videoRef.current) {
            if (document.fullscreenElement) {
                document.exitFullscreen();
            } else {
                videoRef.current.requestFullscreen();
            }
        }
    };

    const togglePictureInPicture = async () => {
        try {
            if (videoRef.current) {
                if (document.pictureInPictureElement) {
                    await document.exitPictureInPicture();
                } else {
                    await videoRef.current.requestPictureInPicture();
                }
            }
        } catch (error) {
            console.error('Picture-in-Picture error:', error);
        }
    };

    const skip = (seconds) => {
        if (videoRef.current) {
            videoRef.current.currentTime += seconds;
        }
    };

    const changePlaybackRate = (rate) => {
        setPlaybackRate(rate);
        if (videoRef.current) {
            videoRef.current.playbackRate = rate;
        }
        setShowSettings(false);
    };

    const formatTime = (time) => {
        if (isNaN(time)) return '0:00';
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className="bg-black rounded-lg overflow-hidden relative group">
            {/* Thumbnail Display (before video starts) */}
            {!hasStarted && thumbnail && (
                <div className="absolute inset-0 z-10">
                    <img
                        src={thumbnail}
                        alt="Course thumbnail"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.style.display = 'none';
                        }}
                    />
                </div>
            )}

            {/* Fallback thumbnail if no image provided */}
            {!hasStarted && !thumbnail && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-gradient-to-br from-blue-300 to-blue-400">
                    <div className="text-center">
                        <PlayIcon className="h-20 w-20 text-white mx-auto mb-4" />
                        <p className="text-white text-lg">Select a lecture to start learning</p>
                    </div>
                </div>
            )}

            {/* Video Element */}
            <video
                ref={videoRef}
                className="w-full aspect-video"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={handleVideoEnded}
                onClick={togglePlay}
            >
                <source src={videoUrl || 'https://www.w3schools.com/html/mov_bbb.mp4'} type="video/mp4" />
                Your browser does not support the video tag.
            </video>

            {/* Video Controls Overlay */}
            <div
                className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4 transition-opacity duration-300"
                onMouseEnter={() => setShowControls(true)}
            >
                {/* Progress Bar */}
                <div className="mb-4">
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={progress}
                        onChange={handleSeek}
                        className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        style={{
                            background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${progress}%, #4b5563 ${progress}%, #4b5563 100%)`
                        }}
                    />
                </div>

                {/* Controls */}
                <div className="flex items-center justify-between">
                    {/* Left Controls */}
                    <div className="flex items-center space-x-3">
                        {/* Play/Pause */}
                        <button
                            onClick={togglePlay}
                            className="text-white hover:text-blue-400 transition-colors"
                        >
                            {isPlaying ? (
                                <PauseIcon className="h-8 w-8" />
                            ) : (
                                <PlayIcon className="h-8 w-8" />
                            )}
                        </button>

                        {/* Skip Backward */}
                        <button
                            onClick={() => skip(-10)}
                            className="text-white hover:text-blue-400 transition-colors"
                        >
                            <BackwardIcon className="h-6 w-6" />
                        </button>

                        {/* Skip Forward */}
                        <button
                            onClick={() => skip(10)}
                            className="text-white hover:text-blue-400 transition-colors"
                        >
                            <ForwardIcon className="h-6 w-6" />
                        </button>

                        {/* Volume */}
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={toggleMute}
                                className="text-white hover:text-blue-400 transition-colors"
                            >
                                {isMuted || volume === 0 ? (
                                    <SpeakerXMarkIcon className="h-6 w-6" />
                                ) : (
                                    <SpeakerWaveIcon className="h-6 w-6" />
                                )}
                            </button>
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.1"
                                value={isMuted ? 0 : volume}
                                onChange={handleVolumeChange}
                                className="w-20 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
                            />
                        </div>

                        {/* Time */}
                        <div className="text-white text-sm">
                            {formatTime(currentTime)} / {formatTime(duration)}
                        </div>
                    </div>

                    {/* Right Controls */}
                    <div className="flex items-center content-center space-x-3">
                        {/* Settings */}
                        <div className="relative">
                            <button
                                onClick={() => setShowSettings(!showSettings)}
                                className="text-white hover:text-blue-400 transition-colors"
                            >
                                <Cog6ToothIcon className="h-6 w-6" />
                            </button>

                            {showSettings && (
                                <div className="absolute bottom-full right-0 mb-2 bg-gray-800 rounded-lg p-2 min-w-[120px]">
                                    <div className="text-white text-sm mb-2">Playback Speed</div>
                                    {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                                        <button
                                            key={rate}
                                            onClick={() => changePlaybackRate(rate)}
                                            className={`w-full text-left px-3 py-1 text-sm rounded hover:bg-gray-700 transition-colors ${playbackRate === rate ? 'text-blue-400' : 'text-white'
                                                }`}
                                        >
                                            {rate}x
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Picture-in-Picture */}
                        <button
                            onClick={togglePictureInPicture}
                            className="text-white hover:text-blue-400 transition-colors"
                        >
                            <RectangleStackIcon className="h-6 w-6" />
                        </button>

                        {/* Fullscreen */}
                        <button
                            onClick={toggleFullscreen}
                            className="text-white hover:text-blue-400 transition-colors"
                        >
                            <ArrowsPointingOutIcon className="h-6 w-6" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Play Button Overlay (when paused) */}
            {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <button
                        onClick={togglePlay}
                        className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-6 transition-colors pointer-events-auto"
                    >
                        <PlayIcon className="h-12 w-12" />
                    </button>
                </div>
            )}

            {/* Animated Play/Pause Overlay */}
            {showPlayPauseAnimation && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                    <div className="animate-ping-once bg-black/50 rounded-full p-8">
                        {isPlaying ? (
                            <PlayIcon className="h-16 w-16 text-white" />
                        ) : (
                            <PauseIcon className="h-16 w-16 text-white" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default CourseVideoPlayer;
